/* eslint-disable react/jsx-props-no-spreading */

import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {NumberInputBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {getDonutArcIndexColor} from '../../../../helpers/analyticsFormatHelpers';
import {
  BucketsPickerProps,
  maybeRenderPickerErrorMessage,
  maybeRenderPickerMetricSubtitle,
  renderPickerButtons,
  StyledPickerInputRowDiv,
  StyledPickerRowColor,
} from './common';

/*
 * Style.
 */

const StyledPickerNumberInputDiv = styled.div<{$isDisabled?: boolean}>`
  width: 48px;
  margin: 0 8px;
  opacity: ${(p) => (p.$isDisabled ? 0.5 : 1)};
`;

/*
 * Constants.
 */

const errorMessage = (
  <Trans id="analytics-element-bucket-number-picker-error-state">Buckets must not overlap each other.</Trans>
);

/*
 * Component.
 */

export const NumberBucketsPicker: FC<BucketsPickerProps> = ({
  metric,
  bucketBoundaries,
  color,
  onRequestClose,
  onApply,
}) => {
  const theme = useUserTheme();

  const metricId = metric.listHistoMetricId;

  const [bucketUpperValues, setBucketUpperValues] = useState<ReadonlyArray<number>>(() => {
    const bucketBoundary = bucketBoundaries?.[metricId];
    if (isUndefined(bucketBoundary)) {
      return [1, 2, 4, 7]; // Shouldn't happen, but matches the default boundaries.
    }

    return bucketBoundary.filter((_boundary, index) => index % 2 === 1); // Keeping only every other values, which are the upper bounds.
  });

  const [shouldShowErrorState, setShouldShowErrorState] = useState<boolean>(false);

  const onApplyClick = () => {
    if (!computeAreBucketsValid(bucketUpperValues)) {
      setShouldShowErrorState(true);
      return;
    }

    onApply(metricId, {
      [metricId]: computeNewBucketValues(bucketUpperValues),
    });
  };

  const setBucketUpperValue = (index: number, value: number) => {
    const newBucketUpperValues = [...bucketUpperValues];
    newBucketUpperValues[index] = value;
    setBucketUpperValues(sanitizeBuckets(newBucketUpperValues, index));
  };

  return (
    <>
      {bucketUpperValues.map((upperValue, index) => {
        // The lowerValue for the first bucket is always 1. Otherwise we add 1 to the previous bucket's upperValue, because the bounds are inclusive.
        // That also means that buckets are continuous and we don't allow gaps between them.
        const lowerValue = index === 0 ? 1 : bucketUpperValues[index - 1] + 1;
        return renderNumberBucketEntryRow({
          index,
          lowerValue,
          upperValue,
          setUpperValue: (value: number | '') =>
            typeof value === 'number' && setBucketUpperValue(index, value),
          error: shouldShowErrorState && upperValue < lowerValue,
          color,
          theme,
        });
      })}
      {/* Last bucket row, doesn't have an upper value */}
      {renderNumberBucketEntryRow({index: 4, lowerValue: bucketUpperValues[3] + 1, color, theme})}
      {maybeRenderPickerMetricSubtitle(metric.metricBucketPickerSubtitle)}
      {maybeRenderPickerErrorMessage(shouldShowErrorState, errorMessage)}
      {renderPickerButtons(onRequestClose, onApplyClick)}
    </>
  );
};

/*
 * Helpers.
 */

const renderNumberBucketEntryRow = ({
  index,
  lowerValue,
  upperValue,
  setUpperValue,
  error,
  color,
  theme,
}: {
  index: number;
  lowerValue: number;
  upperValue?: number;
  setUpperValue?: HandlerOf<number | ''>;
  error?: boolean;
  color: PaletteColorsEnum;
  theme: UserTheme;
}) => (
  <StyledPickerInputRowDiv key={index}>
    <StyledPickerRowColor $color={getDonutArcIndexColor(index, theme, color)} />
    {!isUndefined(setUpperValue) && (
      <Trans id="analytics-histo-bucket-picker-label-between-replies">
        Between
        <StyledPickerNumberInputDiv $isDisabled>
          <NumberInputBox isDisabled value={lowerValue} onChange={setUpperValue} />
        </StyledPickerNumberInputDiv>
        and
        <StyledPickerNumberInputDiv>
          <NumberInputBox value={upperValue} onChange={setUpperValue} hasError={error} />
        </StyledPickerNumberInputDiv>
        replies
      </Trans>
    )}
    {isUndefined(setUpperValue) && (
      <Trans id="analytics-histo-bucket-picker-label-more-replies">
        <StyledPickerNumberInputDiv $isDisabled>
          <NumberInputBox isDisabled value={lowerValue} hasError={error} />
        </StyledPickerNumberInputDiv>
        or more replies
      </Trans>
    )}
  </StyledPickerInputRowDiv>
);

const computeAreBucketsValid = (bucketUpperValues: ReadonlyArray<number>): boolean => {
  // Check that no bucket has an upper limit below its lower limit.
  for (let i = 1; i < bucketUpperValues.length; i++) {
    if (bucketUpperValues[i] <= bucketUpperValues[i - 1]) {
      return false;
    }
  }

  // Check that the first bucket is greater than 0.
  if (bucketUpperValues[0] <= 0) {
    return false;
  }

  return true;
};

const computeNewBucketValues = (bucketUpperValues: ReadonlyArray<number>): ReadonlyArray<number> =>
  // Re-compute lower values from upper values (it's always 1 more than the previous upper value, starting at 1).
  bucketUpperValues
    .map((upperValue, index) => {
      if (index === 0) {
        return [1, upperValue];
      }

      if (index === bucketUpperValues.length - 1) {
        return [bucketUpperValues[index - 1] + 1, upperValue, upperValue + 1]; // Manually adding one entry for the last upper limit ("n" and more).
      }

      return [bucketUpperValues[index - 1] + 1, upperValue];
    })
    .flat();

const sanitizeBuckets = (buckets: ReadonlyArray<number>, updatedIndex: number): ReadonlyArray<number> => {
  // For each bucket, make sure we don't have overlapping values for a better UX.
  const sanitizedBuckets = [...buckets];
  // Sanitize buckets before the updated one.
  for (let i = updatedIndex; i > 0; i--) {
    if (sanitizedBuckets[i - 1] >= sanitizedBuckets[i]) {
      sanitizedBuckets[i - 1] = Math.max(sanitizedBuckets[i] - 1, 0);
    }
  }
  // Sanitize buckets after the updated one.
  for (let i = updatedIndex; i < sanitizedBuckets.length - 1; i++) {
    if (sanitizedBuckets[i] >= sanitizedBuckets[i + 1]) {
      sanitizedBuckets[i + 1] = sanitizedBuckets[i] + 1;
    }
  }
  return sanitizedBuckets;
};
